'use client'
import { type FC, type PropsWithChildren, useCallback, useRef } from 'react'
import { GrandSection } from 'ui'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@chakra-ui/react'
import { OverridesContextProvider, overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { CheckoutProduct } from 'ecosystem'
import { isInvoiceBilled } from 'ecosystem'
import type { GaPurchaseOrder } from '../../../third-party'
import { useRememberProvisionedAddresses } from '../../hooks'
import { SveaConfirmation } from './SveaConfirmation'
import type { ConfirmationPageStdConfig, ConfirmationPageStdOverrides } from './types'
import { ConfirmationErrorNotFound, ConfirmationErrorUnknown } from './ConfirmationErrors'
import { KlarnaConfirmation } from './KlarnaConfirmation'
import { NexiConfirmation } from './NexiConfirmation'
import { QliroConfirmation } from './QliroConfirmation'
import { BriqpayConfirmation } from './BriqpayConfirmation'
import { InvoiceConfirmation } from './InvoiceConfirmation'

export interface ConfirmationPageStdProps {
  config: ConfirmationPageStdConfig
  onConfirmPurchase?: (order: GaPurchaseOrder, items: CheckoutProduct[]) => boolean
}

const ConfirmationPageStdMain = ({
  children,
  paths
}: PropsWithChildren<Pick<ConfirmationPageStdConfig, 'paths'>>) => {
  const router = useRouter()
  const overrides = useOverridesContext<keyof ConfirmationPageStdOverrides>()

  return (
    <>
      <GrandSection sectionId="mina-sidor">
        <Button mx="auto" onClick={() => router.push(paths.onLoggedIn)} w="200px">
          {overrideText('Mina sidor', overrides?.confirmationPageRedirectBtnLabel)}
        </Button>
      </GrandSection>

      <GrandSection minH={800} sectionId="confirmation">
        {children}
      </GrandSection>
    </>
  )
}

export const ConfirmationPageStd: FC<ConfirmationPageStdProps> = ({
  config,
  onConfirmPurchase
}) => {
  const searchParams = useSearchParams()
  const isPurchaseEventSent = useRef(false)

  // The provisioned order carries the addresses that were really used — strictly better
  // device memory for the next checkout than whatever was last typed into a form.
  useRememberProvisionedAddresses(config.order?.storefrontOrder)

  const handleOnPurchase = useCallback(() => {
    const { storefrontOrder } = config.order ?? {}
    if (!storefrontOrder || !onConfirmPurchase || isPurchaseEventSent.current) {
      return
    }

    const items: CheckoutProduct[] =
      storefrontOrder.items?.map((item) => ({
        productId: item.product.id,
        articleNumber: item.product.articleNumber,
        name: item.product.name,
        brand: item.product.brand?.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        categoryIds: item.product.categoryIds ?? undefined
      })) ?? []

    isPurchaseEventSent.current = onConfirmPurchase(
      {
        id: storefrontOrder.orderNumber!,
        // GA4 reads `value` as the whole transaction, with `shipping` and `tax`
        // as components already inside it — which is exactly what `orderAmount`
        // is on both flows, so it maps across directly.
        totalAmount: storefrontOrder.orderAmount,
        taxAmount: storefrontOrder.orderTaxAmount,
        shippingPrice: storefrontOrder.shippingCost,
        currency: storefrontOrder.currency,
        discountAmount: storefrontOrder.orderDiscount,
        discountCode: storefrontOrder.discountCode,
        shippingMethod: storefrontOrder.shippingInfo?.carrierName,
        paymentMethod: storefrontOrder.paymentProvider
      },
      items
    )
  }, [config.order, isPurchaseEventSent, onConfirmPurchase])

  const isInvoiceOrder = isInvoiceBilled(config.order?.storefrontOrder?.paymentType)

  if (!searchParams?.get('order_id') && !searchParams?.get('invoiceOrder')) {
    return <ConfirmationErrorNotFound />
  }

  const renderError = () => <ConfirmationErrorUnknown message={config.provisionError} />

  const renderContent = () => {
    if (isInvoiceOrder && config.order?.storefrontOrder) {
      return (
        <InvoiceConfirmation
          order={config.order.storefrontOrder}
          onConfirmPurchase={handleOnPurchase}
        />
      )
    }

    switch (config.order?.paymentProviderResult?.providerName ?? 'KLARNA') {
      case 'KLARNA':
        return config.order?.paymentProviderResult?.klarnaResponse ? (
          <KlarnaConfirmation
            klarnaData={config.order?.paymentProviderResult?.klarnaResponse}
            onConfirmPurchase={handleOnPurchase}
          />
        ) : (
          renderError()
        )

      case 'NEXI':
        return config.order?.paymentProviderResult?.nexiResponse ? (
          <NexiConfirmation
            nexiData={config.order?.paymentProviderResult?.nexiResponse}
            onConfirmPurchase={handleOnPurchase}
          />
        ) : (
          renderError()
        )
      case 'QLIRO':
        return config.order?.paymentProviderResult?.qliroResponse ? (
          <QliroConfirmation
            qliroData={config.order?.paymentProviderResult?.qliroResponse}
            onConfirmPurchase={handleOnPurchase}
          />
        ) : (
          renderError()
        )

      case 'BRIQPAY':
        return config.order?.paymentProviderResult?.briqpayResponse ? (
          <BriqpayConfirmation
            briqpayData={config.order?.paymentProviderResult?.briqpayResponse}
            onConfirmPurchase={handleOnPurchase}
            order={config.order?.storefrontOrder}
          />
        ) : (
          renderError()
        )

      case 'SVEA':
        return config.order?.paymentProviderResult?.sveaResponse ? (
          <SveaConfirmation
            sveaData={config.order?.paymentProviderResult?.sveaResponse}
            onConfirmPurchase={handleOnPurchase}
          />
        ) : (
          renderError()
        )

      case 'LEDYER':
        return config.order?.paymentProviderResult?.ledyerResponse ? (
          <>LEDYER confirmation</>
        ) : (
          renderError()
        )
    }
  }

  return (
    <OverridesContextProvider overrides={{ ...config.overrides }}>
      <ConfirmationPageStdMain paths={config.paths}>{renderContent()}</ConfirmationPageStdMain>
    </OverridesContextProvider>
  )
}

export const ConfirmationPageStdSkeleton = ({
  paths
}: Pick<ConfirmationPageStdConfig, 'paths'>) => {
  return <ConfirmationPageStdMain paths={paths} />
}
